import React, { useState } from 'react';
import {
  BarChart3,
  ChevronDown,
  Clock,
  Copy,
  Crosshair,
  Download,
  Folder,
  LayoutGrid,
  List,
  LocateFixed,
  MapPin,
  Minus,
  MoreVertical,
  Navigation,
  Pencil,
  Play,
  Plus,
  Search,
  Square,
  Star,
  Timer,
  Upload,
  X,
} from 'lucide-react';
import {
  COLLECTIONS,
  formatAccuracy,
  POINT_COLOR,
  formatCoords,
  formatLastUsed,
  formatMockTime,
  pointInsights,
  USAGE_DAYS,
  type PointInsights,
  type StationaryPoint,
} from '../../data/stationary-mock';
import StationaryMap, { type MapStyleName, type StationaryMapHandle } from './StationaryMap';

type ListFilter = 'all' | 'recent' | 'favourite' | 'never' | 'most';
type ListSort = 'recent' | 'name' | 'times' | 'accuracy';
type MenuSource = 'row' | 'detail' | 'float';
type DetailTab = 'overview' | 'usage' | 'activity' | 'nearby';
type PointLayout = 'list' | 'grid';

const PHOTOS = [
  'https://images.unsplash.com/photo-1587474260584-136574528ed5?auto=format&fit=crop&w=240&q=60',
  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=240&q=60',
  'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=240&q=60',
  'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?auto=format&fit=crop&w=240&q=60',
  'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=240&q=60',
  'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=240&q=60',
];

const PHOTO_INDEX: Record<string, number> = {
  home: 0,
  office: 1,
  'india-gate': 2,
  connaught: 3,
  gym: 4,
};

export interface StationaryBoardProps {
  visible: StationaryPoint[];
  allPoints: StationaryPoint[];
  selected: StationaryPoint | null;
  counts: { total: number; favourites: number; never: number };
  filter: ListFilter;
  sort: ListSort;
  layout: PointLayout;
  grouped: boolean;
  tableQuery: string;
  areaQuery: string;
  city: string;
  cities: string[];
  activeCollectionName: string | null;
  moreFilters: boolean;
  mobilePane: 'points' | 'details';
  mapStyle: MapStyleName;
  detailTab: DetailTab;
  mockingId: string | null;
  menu: { pointId: string; source: MenuSource } | null;
  mapRef: React.RefObject<StationaryMapHandle | null>;
  fileRef: React.RefObject<HTMLInputElement | null>;
  onTableQuery(value: string): void;
  onAreaQuery(value: string): void;
  onFilter(filter: ListFilter): void;
  onSort(sort: ListSort): void;
  onCity(city: string): void;
  onLayout(layout: PointLayout): void;
  onGrouped(grouped: boolean): void;
  onMoreFilters(open: boolean): void;
  onMobilePane(pane: 'points' | 'details'): void;
  onMapStyle(style: MapStyleName): void;
  onDetailTab(tab: DetailTab): void;
  onSelect(id: string): void;
  onOpenMenu(pointId: string, source: MenuSource, target: HTMLElement): void;
  onToggleMock(point: StationaryPoint): void;
  onToggleFavourite(pointId: string): void;
  onEdit(pointId: string): void;
  onCopyCoords(point: StationaryPoint): void;
  onAdd(): void;
  onImport(event: React.ChangeEvent<HTMLInputElement>): void;
  onExport(): void;
  onCollections(): void;
  onClearCollection(): void;
  onShowAll(): void;
  onClearFilters(): void;
}

function pointPhoto(point: StationaryPoint): string {
  const index = PHOTO_INDEX[point.id] ?? point.name.length % PHOTOS.length;
  return PHOTOS[index];
}

function PointThumb({ point, large = false }: { point: StationaryPoint; large?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className={`sp-thumb sp-thumb--${point.color}${large ? ' is-large' : ''}`} aria-hidden="true">
        <MapPin size={large ? 22 : 16} />
      </span>
    );
  }
  return (
    <img
      className={`sp-thumb${large ? ' is-large' : ''}`}
      src={pointPhoto(point)}
      alt=""
      onError={() => setFailed(true)}
    />
  );
}

function PointMeta({ point }: { point: StationaryPoint }) {
  return (
    <span className="sp-point__meta">
      <span><BarChart3 size={12} aria-hidden="true" />{point.timesUsed}</span>
      <span><Timer size={12} aria-hidden="true" />{formatMockTime(point.totalMockHours)}</span>
      <span><Crosshair size={12} aria-hidden="true" />{formatAccuracy(point.accuracyMeters)}</span>
    </span>
  );
}

function donutBackground(insights: PointInsights): string {
  const total = insights.buckets.reduce((sum, bucket) => sum + bucket.count, 0) || 1;
  let cursor = 0;
  const stops = insights.buckets.map((bucket) => {
    const start = cursor;
    cursor += (bucket.count / total) * 100;
    return `${bucket.color} ${start}% ${cursor}%`;
  });
  return `conic-gradient(${stops.join(',')})`;
}

function openDirections(point: StationaryPoint) {
  const url = `https://www.google.com/maps/dir/?api=1&destination=${point.latitude},${point.longitude}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

const StationaryBoard: React.FC<StationaryBoardProps> = ({
  visible,
  allPoints,
  selected,
  counts,
  filter,
  sort,
  layout,
  grouped,
  tableQuery,
  areaQuery,
  city,
  cities,
  activeCollectionName,
  moreFilters,
  mobilePane,
  mapStyle,
  detailTab,
  mockingId,
  menu,
  mapRef,
  fileRef,
  onTableQuery,
  onAreaQuery,
  onFilter,
  onSort,
  onCity,
  onLayout,
  onGrouped,
  onMoreFilters,
  onMobilePane,
  onMapStyle,
  onDetailTab,
  onSelect,
  onOpenMenu,
  onToggleMock,
  onToggleFavourite,
  onEdit,
  onCopyCoords,
  onAdd,
  onImport,
  onExport,
  onCollections,
  onClearCollection,
  onShowAll,
  onClearFilters,
}) => {
  const insights = selected ? pointInsights(selected) : null;
  const trendMax = insights ? Math.max(1, ...insights.trend.map((bar) => bar.value)) : 1;
  const usageMax = selected ? Math.max(1, ...selected.usage) : 1;
  const nearby = selected
    ? allPoints.filter((point) => point.id !== selected.id && point.city === selected.city).slice(0, 6)
    : [];
  const sessionTotal = insights ? insights.buckets.reduce((sum, bucket) => sum + bucket.count, 0) : 0;

  return (
    <>
      <header className="sp-head">
        <div className="sp-head__actions">
          <button type="button" className="sp-btn sp-btn--primary" onClick={onAdd}>
            <Plus size={16} aria-hidden="true" />
            Add Stationary Point
          </button>
          <button type="button" className="sp-btn sp-btn--ghost" onClick={() => fileRef.current?.click()}>
            <Upload size={16} aria-hidden="true" />
            Import (CSV)
          </button>
          <button type="button" className="sp-btn sp-btn--ghost" onClick={onExport}>
            <Download size={16} aria-hidden="true" />
            Export (CSV)
          </button>
          <button type="button" className="sp-btn sp-btn--ghost" onClick={onCollections}>
            <Folder size={16} aria-hidden="true" />
            Collections
          </button>
          <input
            ref={fileRef as React.Ref<HTMLInputElement>}
            className="visually-hidden"
            type="file"
            accept=".csv,text/csv"
            aria-label="Import CSV file"
            onChange={onImport}
          />
        </div>
      </header>

      <div className={`sp-workspace${mobilePane === 'details' ? ' is-details' : ' is-points'}`}>
        <aside className="sp-list" aria-label="Saved points">
          <div className="sp-list__head">
            <h2>All Points ({counts.total})</h2>
            <label className="sp-search">
              <Search size={16} aria-hidden="true" />
              <span className="visually-hidden">Search stationary points</span>
              <input
                type="search"
                value={tableQuery}
                placeholder="Search points or address..."
                onChange={(event) => onTableQuery(event.target.value)}
              />
            </label>
            <div className="sp-chips" role="group" aria-label="Point filters">
              <button type="button" className="sp-chip" aria-pressed={filter === 'all'} onClick={() => onFilter('all')}>
                All {counts.total}
              </button>
              <button type="button" className="sp-chip" aria-pressed={filter === 'favourite'} onClick={() => onFilter('favourite')}>
                <Star size={12} aria-hidden="true" />
                Favourites {counts.favourites}
              </button>
              <button type="button" className="sp-chip" aria-pressed={filter === 'most'} onClick={() => onFilter('most')}>
                Most Used
              </button>
              <button type="button" className="sp-chip" aria-pressed={filter === 'recent'} onClick={() => onFilter('recent')}>
                Recent
              </button>
              <button
                type="button"
                className="sp-chip sp-chip--icon"
                aria-label="More filters"
                aria-expanded={moreFilters}
                onClick={() => onMoreFilters(!moreFilters)}
              >
                <ChevronDown size={14} aria-hidden="true" />
              </button>
            </div>
            {moreFilters && (
              <div className="sp-more-filters">
                <button type="button" className="sp-chip" aria-pressed={filter === 'never'} onClick={() => onFilter('never')}>
                  Never Used ({counts.never})
                </button>
                <label className="sp-select">
                  <span className="visually-hidden">City</span>
                  <select aria-label="City" value={city} onChange={(event) => onCity(event.target.value)}>
                    <option value="">All Cities</option>
                    {cities.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </label>
              </div>
            )}
            {activeCollectionName && (
              <button type="button" className="sp-chip" aria-pressed="true" onClick={onClearCollection}>
                {activeCollectionName}
                <X size={12} aria-hidden="true" />
              </button>
            )}
            <div className="sp-list__tools">
              <label className="sp-select">
                <span>Sort by</span>
                <select aria-label="Sort by" value={sort} onChange={(event) => onSort(event.target.value as ListSort)}>
                  <option value="recent">Last used</option>
                  <option value="name">Name</option>
                  <option value="times">Times used</option>
                  <option value="accuracy">Accuracy</option>
                </select>
              </label>
              <div className="sp-view" role="group" aria-label="List layout">
                <button type="button" aria-label="List view" aria-pressed={layout === 'list'} onClick={() => onLayout('list')}>
                  <List size={16} aria-hidden="true" />
                </button>
                <button type="button" aria-label="Grid view" aria-pressed={layout === 'grid'} onClick={() => onLayout('grid')}>
                  <LayoutGrid size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          <div className={`sp-list__scroll${layout === 'grid' ? ' is-grid' : ''}`}>
            {visible.length === 0 ? (
              <div className="sp-empty">
                <p>{allPoints.length === 0 ? 'No stationary points yet.' : 'No stationary points match these filters.'}</p>
                {allPoints.length > 0 && (
                  <button type="button" className="sp-btn sp-btn--ghost" onClick={onClearFilters}>Clear filters</button>
                )}
              </div>
            ) : visible.map((point) => {
              const isSelected = point.id === selected?.id;
              return (
                <div
                  key={point.id}
                  className={`sp-point${isSelected ? ' is-selected' : ''}${layout === 'grid' ? ' is-grid' : ''}`}
                >
                  <button type="button" className="sp-point__open" aria-pressed={isSelected} onClick={() => onSelect(point.id)}>
                    <PointThumb point={point} />
                    <span className="sp-point__body">
                      <span className="sp-point__title">
                        <i className="sp-dot" style={{ background: POINT_COLOR[point.color] }} aria-hidden="true" />
                        <strong>{point.name}</strong>
                        {point.favourite && <Star className="sp-namecell__star" size={14} fill="currentColor" aria-hidden="true" />}
                      </span>
                      <small>{point.address}</small>
                      <PointMeta point={point} />
                    </span>
                  </button>
                  <button
                    type="button"
                    className="sp-more"
                    data-point-menu=""
                    aria-label={`Actions for ${point.name}`}
                    aria-haspopup="menu"
                    aria-expanded={menu?.pointId === point.id && menu.source === 'row'}
                    aria-controls="stationary-point-menu"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpenMenu(point.id, 'row', event.currentTarget);
                    }}
                  >
                    <MoreVertical size={18} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
        </aside>

        <div className="sp-stage">
          <section className="sp-card sp-map" aria-label="Stationary points map">
            <StationaryMap
              ref={mapRef as React.Ref<StationaryMapHandle>}
              points={visible}
              selectedId={selected?.id ?? null}
              mapStyle={mapStyle}
              grouped={grouped}
              onSelect={onSelect}
            />
            <div className="sp-map__controls">
              <div className="sp-styles" role="group" aria-label="Map style">
                {(['map', 'satellite', 'terrain'] as MapStyleName[]).map((style) => (
                  <button key={style} type="button" aria-pressed={mapStyle === style} onClick={() => onMapStyle(style)}>
                    {style === 'map' ? 'Map' : style === 'satellite' ? 'Satellite' : 'Terrain'}
                  </button>
                ))}
              </div>
              <div className="sp-area-bar">
                <label className="sp-area">
                  <Search size={14} aria-hidden="true" />
                  <span className="visually-hidden">Search this area</span>
                  <input
                    type="search"
                    value={areaQuery}
                    placeholder="Search this area"
                    onChange={(event) => onAreaQuery(event.target.value)}
                  />
                </label>
                <button type="button" className="sp-btn sp-btn--ghost sp-show-all" aria-label="Show all points" onClick={onShowAll}>
                  <span className="sp-show-all__full">Show All</span>
                  <span className="sp-show-all__short">All</span>
                </button>
              </div>
              <ul className="sp-legend" aria-label="Map legend">
                <li><button type="button" aria-label={`Show all ${counts.total} points`} onClick={() => onFilter('all')}><i className="sp-dot" style={{ background: '#64748b' }} />All Points ({counts.total})</button></li>
                <li><button type="button" aria-label={`Show ${counts.favourites} favourites`} onClick={() => onFilter('favourite')}><i className="sp-dot" style={{ background: '#eab308' }} />Favourites ({counts.favourites})</button></li>
                <li><button type="button" aria-label="Show most used points" onClick={() => onFilter('most')}><i className="sp-dot" style={{ background: 'var(--ml-primary, #8b5cf6)' }} />Most Used</button></li>
                <li><button type="button" aria-label="Show recently used points" onClick={() => onFilter('recent')}><i className="sp-dot" style={{ background: '#f97316' }} />Recently Used</button></li>
                <li><button type="button" aria-label="Open collections" onClick={onCollections}><i className="sp-dot" style={{ background: 'var(--ml-info, #3b82f6)' }} />Collections</button></li>
              </ul>
              <div className="sp-zoom">
                <button type="button" aria-label="Zoom in" onClick={() => mapRef.current?.zoomBy(1)}>
                  <Plus size={16} aria-hidden="true" />
                </button>
                <button type="button" aria-label="Zoom out" onClick={() => mapRef.current?.zoomBy(-1)}>
                  <Minus size={16} aria-hidden="true" />
                </button>
                <button type="button" aria-label="Fit all points" onClick={() => mapRef.current?.fitAll()}>
                  <LocateFixed size={16} aria-hidden="true" />
                </button>
              </div>
              <button
                type="button"
                className="sp-group"
                role="switch"
                aria-label="Group Nearby Points"
                aria-checked={grouped}
                onClick={() => onGrouped(!grouped)}
              >
                <span className="sp-group__label">Group Nearby Points</span>
                <span className="sp-group__short">Group</span>
                <span className={`sp-group__knob${grouped ? ' is-on' : ''}`} aria-hidden="true" />
              </button>
              {selected && (
                <article className="sp-float">
                  <PointThumb point={selected} />
                  <div>
                    <strong>{selected.name}</strong>
                    <small>{selected.address}</small>
                    <PointMeta point={selected} />
                  </div>
                  <div className="sp-float__actions">
                    <button
                      type="button"
                      className={`sp-btn ${mockingId === selected.id ? 'sp-btn--stop' : 'sp-btn--primary'}`}
                      onClick={() => onToggleMock(selected)}
                    >
                      {mockingId === selected.id ? <Square size={14} aria-hidden="true" /> : <Play size={14} aria-hidden="true" />}
                      {mockingId === selected.id ? 'Stop Mock' : 'Start Mock'}
                    </button>
                    <button type="button" className="sp-icon-btn" aria-label={`Edit ${selected.name}`} onClick={() => onEdit(selected.id)}>
                      <Pencil size={15} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="sp-icon-btn"
                      data-point-menu=""
                      aria-label={`Actions for ${selected.name}`}
                      aria-haspopup="menu"
                      aria-expanded={menu?.pointId === selected.id && menu.source === 'float'}
                      onClick={(event) => onOpenMenu(selected.id, 'float', event.currentTarget)}
                    >
                      <MoreVertical size={15} aria-hidden="true" />
                    </button>
                  </div>
                </article>
              )}
            </div>
          </section>

          <section className="sp-card sp-detail" aria-label="Selected point">
            {selected && insights ? (
              <>
                <div className="sp-detail__bar">
                  <PointThumb point={selected} large />
                  <div className="sp-detail__id">
                    <div className="sp-detail__name">
                      <h2>{selected.name}</h2>
                      <button
                        type="button"
                        className={`sp-star${selected.favourite ? ' is-on' : ''}`}
                        aria-label={selected.favourite ? 'Remove from favourites' : 'Add to favourites'}
                        aria-pressed={selected.favourite}
                        onClick={() => onToggleFavourite(selected.id)}
                      >
                        <Star size={16} fill={selected.favourite ? 'currentColor' : 'none'} aria-hidden="true" />
                      </button>
                      {mockingId === selected.id && <span className="sp-live"><i />Active</span>}
                    </div>
                    <p>{selected.address}</p>
                    <p className="sp-coords">
                      <span>{formatCoords(selected.latitude, selected.longitude)}</span>
                      <button type="button" aria-label="Copy coordinates" onClick={() => onCopyCoords(selected)}>
                        <Copy size={14} aria-hidden="true" />
                      </button>
                    </p>
                  </div>
                  <div className="sp-detail__actions">
                    <button
                      type="button"
                      className={`sp-btn ${mockingId === selected.id ? 'sp-btn--stop' : 'sp-btn--primary'}`}
                      onClick={() => onToggleMock(selected)}
                    >
                      {mockingId === selected.id ? <Square size={15} aria-hidden="true" /> : <Play size={15} aria-hidden="true" />}
                      {mockingId === selected.id ? 'Stop Mock' : 'Start Mock'}
                    </button>
                    <button type="button" className="sp-btn sp-btn--ghost" onClick={() => onEdit(selected.id)}>
                      <Pencil size={15} aria-hidden="true" />
                      Edit
                    </button>
                    <button type="button" className="sp-btn sp-btn--ghost" onClick={() => openDirections(selected)}>
                      <Navigation size={15} aria-hidden="true" />
                      Directions
                    </button>
                    <button
                      type="button"
                      className="sp-more"
                      data-point-menu=""
                      aria-label={`Actions for ${selected.name}`}
                      aria-haspopup="menu"
                      aria-expanded={menu?.pointId === selected.id && menu.source === 'detail'}
                      aria-controls="stationary-point-menu"
                      onClick={(event) => onOpenMenu(selected.id, 'detail', event.currentTarget)}
                    >
                      <MoreVertical size={18} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="sp-tabs" role="tablist" aria-label="Point details">
                  {([
                    ['overview', 'Overview'],
                    ['usage', 'Usage Stats'],
                    ['activity', 'Activity History'],
                    ['nearby', 'Nearby Places'],
                  ] as const).map(([id, label]) => (
                    <button key={id} type="button" role="tab" aria-selected={detailTab === id} onClick={() => onDetailTab(id)}>
                      {label}
                    </button>
                  ))}
                </div>

                {detailTab === 'overview' && (
                  <div className="sp-panel" role="tabpanel">
                    <div className="sp-kpis">
                      <article>
                        <span className="sp-kpi__label"><BarChart3 size={14} aria-hidden="true" /><small>Times Used</small></span>
                        <strong>{selected.timesUsed}</strong>
                        <em>+{insights.weekUses} this week</em>
                      </article>
                      <article>
                        <span className="sp-kpi__label"><Timer size={14} aria-hidden="true" /><small>Total Mock Time</small></span>
                        <strong>{formatMockTime(selected.totalMockHours)}</strong>
                        <em>+{insights.hourDelta}h</em>
                      </article>
                      <article>
                        <span className="sp-kpi__label"><Clock size={14} aria-hidden="true" /><small>Last Used</small></span>
                        <strong>{formatLastUsed(selected.lastUsedHours)}</strong>
                        <em>{selected.createdLabel}</em>
                      </article>
                      <article>
                        <span className="sp-kpi__label"><Crosshair size={14} aria-hidden="true" /><small>Avg. Session</small></span>
                        <strong>{insights.avgMinutes} min</strong>
                        <em className={insights.avgDelta < 0 ? 'is-down' : ''}>{insights.avgDelta > 0 ? '+' : ''}{insights.avgDelta}%</em>
                      </article>
                    </div>
                    <div className="sp-charts">
                      <section>
                        <h3>Usage Trend (Last 30 Days)</h3>
                        <div className="sp-trend" aria-hidden="true">
                          {insights.trend.map((bar) => (
                            <div key={bar.label}>
                              <span><i style={{ height: `${Math.max(8, (bar.value / trendMax) * 100)}%` }} /></span>
                              <small>{bar.label}</small>
                            </div>
                          ))}
                        </div>
                      </section>
                      <section>
                        <div className="sp-chart-head">
                          <h3>Session Distribution</h3>
                          <span>Last 30 Days</span>
                        </div>
                        <div className="sp-donut-row">
                          <div className="sp-donut" style={{ background: donutBackground(insights) }}>
                            <span><strong>{sessionTotal}</strong><small>Sessions</small></span>
                          </div>
                          <ul>
                            {insights.buckets.map((bucket) => (
                              <li key={bucket.label}>
                                <i style={{ background: bucket.color }} />
                                {bucket.label}
                                <b>{bucket.count}</b>
                                <span className="sp-donut__pct">({sessionTotal === 0 ? 0 : Math.round((bucket.count / sessionTotal) * 100)}%)</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </section>
                    </div>
                  </div>
                )}

                {detailTab === 'usage' && (
                  <div className="sp-panel" role="tabpanel">
                    <p className="sp-usage-label">Last 7 days</p>
                    <div className="sp-bars" aria-hidden="true">
                      {selected.usage.map((value, index) => (
                        <div key={USAGE_DAYS[index]}>
                          <span className="sp-bars__track">
                            <i style={{ height: `${Math.max(6, (value / usageMax) * 100)}%` }} />
                          </span>
                          <small>{USAGE_DAYS[index]}</small>
                        </div>
                      ))}
                    </div>
                    <p className="sp-usage-total">
                      Total mock time
                      <strong>{formatMockTime(selected.totalMockHours)}</strong>
                    </p>
                  </div>
                )}

                {detailTab === 'activity' && (
                  <ol className="sp-timeline" role="tabpanel">
                    {selected.activity.map((event, index) => (
                      <li key={event.id}>
                        <span className="sp-timeline__rail" aria-hidden="true">
                          <i className={`sp-timeline__dot sp-timeline__dot--${event.tone}`} />
                          {index < selected.activity.length - 1 && <span className="sp-timeline__line" />}
                        </span>
                        <span>
                          <strong>{event.title}</strong>
                          <small>{event.time}</small>
                        </span>
                      </li>
                    ))}
                  </ol>
                )}

                {detailTab === 'nearby' && (
                  <div className="sp-panel" role="tabpanel">
                    {nearby.length === 0 ? (
                      <p className="sp-notes">No other saved points in {selected.city}.</p>
                    ) : (
                      <ul className="sp-nearby">
                        {nearby.map((point) => (
                          <li key={point.id}>
                            <button type="button" onClick={() => onSelect(point.id)}>
                              <i className="sp-dot" style={{ background: POINT_COLOR[point.color] }} aria-hidden="true" />
                              <span>
                                <strong>{point.name}</strong>
                                <small>{point.address}</small>
                              </span>
                              <em>{formatAccuracy(point.accuracyMeters)}</em>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <p className="sp-notes">{selected.notes || 'No notes yet.'}</p>
                    <p className="sp-notes">
                      {selected.collectionIds.length === 0
                        ? 'Not in a collection'
                        : selected.collectionIds
                          .map((id) => COLLECTIONS.find((collection) => collection.id === id)?.name ?? id)
                          .join(', ')}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="sp-detail__empty">
                <MapPin size={28} aria-hidden="true" />
                <h2>No point selected</h2>
                <p>Add a stationary point or clear the filters to see one here.</p>
              </div>
            )}
          </section>
        </div>

        <div className="sp-pane-switch" role="tablist" aria-label="Mobile panes">
          <button type="button" role="tab" aria-selected={mobilePane === 'points'} onClick={() => onMobilePane('points')}>
            Points
          </button>
          <button type="button" role="tab" aria-selected={mobilePane === 'details'} onClick={() => onMobilePane('details')}>
            Details
          </button>
        </div>
      </div>
    </>
  );
};

export default StationaryBoard;
